/**
 * Presenting a tenant's deployment environment. Pure — no React, no fetching.
 *
 * The backend registry (`GET /api/admin/environments`) is the authority for
 * which environments exist and whether each one's runtime is attached; this
 * module only turns a code into words and a badge tone, so the list row, the
 * detail header and the Create screen cannot each invent their own.
 */

/** Canonical order, LIVE first because it is the default. */
export const ENVIRONMENT_ORDER = ['LIVE', 'DEVELOPMENT', 'STAGING', 'DEMO'];

export const DEFAULT_ENVIRONMENT = 'LIVE';

const PRESENTATION = {
  LIVE: { label: 'Live', tone: 'success' },
  DEVELOPMENT: { label: 'Development', tone: 'violet' },
  STAGING: { label: 'Staging', tone: 'sky' },
  DEMO: { label: 'Demo', tone: 'warning' },
};

/** A tenant that predates environments. NOT silently "Live". */
export const UNCLASSIFIED = { code: null, label: 'Unclassified', tone: 'muted' };

export function normalizeEnvironment(code) {
  if (code === null || code === undefined || code === '') return null;
  const upper = String(code).trim().toUpperCase();
  return PRESENTATION[upper] ? upper : null;
}

/** `{code, label, tone}` for a stored value; Unclassified for anything absent or unknown. */
export function describeEnvironment(code) {
  const norm = normalizeEnvironment(code);
  if (!norm) return UNCLASSIFIED;
  return { code: norm, ...PRESENTATION[norm] };
}

/** Sample Credentials never attach to a Live tenant — mirrors the backend rule. */
export function sampleCredentialsAllowed(code) {
  const norm = normalizeEnvironment(code);
  return norm !== null && norm !== 'LIVE';
}

/* ──────────────────────────────────────────────────────────────────────────
 * WHAT USED TO BE HERE, AND WHY IT IS NOT
 *
 * `SAMPLE_CONNECTION_TAGS` — five connection names written down in this file
 * and rendered under "Will connect automatically", counted in the review line
 * as "On · 5 connections". It said five whether the selected profile held
 * five, held nothing at all, or could not be reached: DESIRED state rendered
 * as though it had already succeeded, which is the defect the Demo Connections
 * closure programme exists to remove. A constant cannot be wrong about a
 * runtime it never read.
 *
 * The roster is server-supplied and per-profile now, produced by the SAME
 * flattener the approval freeze uses, so what an operator is shown before
 * approving and what is recorded when they approve cannot come from two
 * definitions that drift:
 *
 *   GET /api/admin/sample-connection-profiles?environment=<ENV>
 *       -> profiles[].desired_items[]  { provider, connection, label,
 *                                        credential_imported_at_selection }
 *       -> profiles[].desired_item_count, profiles[].profile_fingerprint
 *          consumed by `sampleConnectionPresentation.js` (Create Tenant).
 *
 *   GET /api/admin/tenants/{id}/demo-connections
 *       -> desired / attempted / actual, with per-item outcomes
 *          consumed by `demoConnectionsPresentation.js` (Tenant Detail).
 *
 * Value vocabularies are pinned in `demoConnectionsContract.json` beside this
 * file, generated from `backend/modules/admin/services/demo_profile_intent.py`.
 * Nothing on the frontend may invent a status or an outcome value.
 * ────────────────────────────────────────────────────────────────────────── */
